import {ContentLearningEventShowResponseOrdersInner, ContentLearningEventUserSearchInOrderCompanySpaceUserSearchResponse, type SpaceUserCreateResponse} from "generated-api";
import {useAppTranslation} from "services/i18n";
import {useAppDispatch} from "store";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {isApiResultError} from "../../helpers/api";
import {Formik, FormikErrors, FormikProps} from "formik";
import {Dialog, DialogActions, DialogContent, DialogTitle, Grid, LinearProgress, Paper} from "@mui/material";
import Button from "@mui/material/Button";
import 'react-quill/dist/quill.snow.css';
import IconButton from "@mui/material/IconButton";
import {Close} from "@mui/icons-material";
import {createLearningEventParticipant, searchUserForLearningEvent} from "store/userGroup";
import {defaultDialogStyle} from "utils/dialog";
import {TextFormField} from "components/form/TextFormField";
import {addMessage} from "store/localApp";
import {CheckboxFormField} from "components/form/CheckboxFormField";
import {SelectFormField} from "components/form/SelectFormField";
import {OptionValue} from "model/form";
import {createUser} from "store/user";

const FAKE_VALUE_INVITE = -1;

interface ValuesType {
    search?: string,
    email?: string,
    user_id?: number,
    name?: string,
    invitationLink: string
    sendInvite?: boolean,
}

const dialogStyle = defaultDialogStyle();

export const OrderParticipantDialog = ({order, learningEventId, onSuccess, onClose}: {
    order: ContentLearningEventShowResponseOrdersInner,
    learningEventId: number,
    onSuccess: (result: any) => void,
    onClose: () => void
}) => {
    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const [isSaving, setIsSaving] = useState(false);

    // user autocomplete
    const [isUserLoading, setIsUserLoading] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    const [userOptions, setUserOptions] = useState<OptionValue[]>([]);
    const [isInviting, setIsInviting] = useState(false);

    const initialValues: ValuesType = useMemo(() => {
        return {
            invitationLink: order.invitation_link
        }
    }, [order.invitation_link]);

    const handleCopyLink = useCallback(async () => {
        await navigator.clipboard.writeText(order.invitation_link);
        dispatch(addMessage({
            severity: 'success',
            title: t('userGroup.registration.link.copied')
        }));
    }, [order.invitation_link, dispatch, t]);

    const handleEmailChange = useCallback((setFieldValue: FormikProps<ValuesType>['setFieldValue']) => async (value: any) => {
        const o = userOptions?.find(o => o.value === value);
        setFieldValue('user_id', o?.value, true);

        if (o?.value === FAKE_VALUE_INVITE) {
            setFieldValue('email', o.tooltip && o.tooltip.indexOf('@') >= 0 ? o.tooltip : undefined);
            setFieldValue('name', o.tooltip && o.tooltip.indexOf('@') >= 0 ? undefined : o.tooltip);
            setIsInviting(true);

        } else {
            setIsInviting(false);
            // const user = o?.params?.user as ContentLearningEventUserSearchInOrderCompanySpaceUserSearchResponse;
            // setFieldValue('email', user?.email);
            // setFieldValue('name', user?.full_name);
        }
    }, [userOptions]);

    const handleSave = useCallback(async (values: ValuesType) => {

        setIsSaving(true);

        let userId;
        if (isInviting) {
            const res = await dispatch(createUser({
                body: {
                    email: values.email!,
                    full_name: values.name!,
                    role: "participant",
                    invitation: false
                }
            }));
            if (!isApiResultError(res)) {
                userId = (res.payload as SpaceUserCreateResponse)?.user_in_company_space_id;
            }
        } else {
            userId = values.user_id;
        }

        if (userId && order.company_space?.id) {
            const res = await dispatch(createLearningEventParticipant({
                learningEventId,
                body: {
                    origin_company_space_id: order.company_space.id,
                    user_in_company_space_id: userId,
                    send_invitation_email: values.sendInvite
                }
            }));
            if (!isApiResultError(res)) {
                onSuccess(res.payload);
                onClose();
            }
        }
        setIsSaving(false);
    }, [isInviting, learningEventId, order.company_space?.id, dispatch, onSuccess, onClose]);

    const handleValidate = useCallback((values: ValuesType) => {
        let errors = {} as FormikErrors<ValuesType>;
        if (isInviting) {
            if (!values.email) {
                errors.email = t('userGroup.registration.addParticipant.email.messages.required')
            }
            if (!values.name) {
                errors.name = t('userGroup.registration.addParticipant.name.messages.required')
            }
        } else {
            if (!values.user_id) {
                errors.search = t('userGroup.registration.addParticipant.user.messages.required')
            }
        }
        return errors;
    }, [isInviting, t]);

    useEffect(() => {
        if (!userSearch) {
            setUserOptions([]);
            return;
        }

        setIsUserLoading(true);
        dispatch(searchUserForLearningEvent({
            email: userSearch,
            orderId: order.id!,
            learningEventId
        })).then((res) => {
            if (!isApiResultError(res) && res.payload) {
                const items = res.payload as ContentLearningEventUserSearchInOrderCompanySpaceUserSearchResponse[];
                const options: OptionValue[] = items
                    .filter(u => !!u.id && !u.existing_participant)
                    .map(u => ({value: u.id, label: u.full_name + ' (' + u.email + ')', params: {user: u}}));
                setUserOptions(current => {
                    const currentlySelected = current.find(o => o.label === userSearch);
                    if (currentlySelected && !options.find(o => o.label === userSearch)) {
                        options.unshift(currentlySelected);
                    }
                    if (!options.length) {
                        options.push({
                            value: FAKE_VALUE_INVITE,
                            label: t('userGroup.registration.addParticipant.user.messages.inviteNew'),
                            htmlLabel: <div>
                                <div>{t('userGroup.registration.addParticipant.user.messages.empty')}</div>
                                <strong>{t('userGroup.registration.addParticipant.user.messages.inviteNew')}</strong>
                            </div>, tooltip: userSearch
                        })
                    }
                    return options;
                });
            } else {
                setUserOptions([]);
            }
        }).finally(() => {
            setIsUserLoading(false);
        })
    }, [learningEventId, order.id, userSearch, dispatch, t]);

    return (
        <Dialog open={true} onClose={onClose} PaperComponent={Paper} sx={dialogStyle} maxWidth={'md'}>
            <Formik
                initialValues={initialValues}
                onSubmit={handleSave}
                validate={handleValidate}
            >
                {({handleSubmit, setFieldValue}) => <form onSubmit={handleSubmit}>
                    <DialogTitle className={'tw-flex tw-w-full tw-justify-between tw-items-center'}>
                        <span>{t('userGroup.registration.addParticipant.label')} - {order.company_space?.name}</span>
                        <IconButton onClick={onClose} sx={{color: 'inherit'}}><Close/></IconButton>
                    </DialogTitle>
                    <LinearProgress hidden={!isSaving}/>
                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                {!isInviting && <SelectFormField
                                    name={'search'}
                                    options={userOptions}
                                    // renderOption={renderUserOption}
                                    onSearch={setUserSearch}
                                    isLoading={isUserLoading}
                                    onChange={handleEmailChange(setFieldValue)}
                                    label={t('userGroup.registration.addParticipant.user.label')}
                                />}
                                {isInviting && <Paper className={'tw-p-20 tw-rounded-none'}>
                                    <h2>{t('userGroup.registration.addParticipant.user.messages.inviteNew')}</h2>
                                    <Grid container spacing={2}>
                                        <Grid size={12}>
                                            <TextFormField name={'email'} label={t('userGroup.registration.addParticipant.email.label')} required/>
                                        </Grid>
                                        <Grid size={12}>
                                            <TextFormField name={'name'} label={t('userGroup.registration.addParticipant.name.label')} required/>
                                        </Grid>
                                        <Grid size={12} className={'tw-flex tw-justify-end'}>
                                            <Button color={'inherit'} onClick={() => {
                                                setUserSearch('');
                                                setFieldValue('search', undefined);
                                                handleEmailChange(setFieldValue)(undefined);
                                            }} disabled={isSaving}>
                                                {t('form.cancel')}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Paper>}
                            </Grid>
                            <Grid size={12}>
                                <Grid container spacing={2}>
                                    <Grid className={'tw-flex-grow'}>
                                        <TextFormField name={'invitationLink'} label={t('userGroup.registration.addParticipant.link')} disabled/>
                                    </Grid>
                                    <Grid className={'tw-flex tw-flex-col tw-justify-center tw-gap-8'}>
                                        <Button color={'dark' as any} variant={'contained'} onClick={handleCopyLink}>
                                            {t('userGroup.registration.addParticipant.copyLink')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={12}>
                                <CheckboxFormField
                                    name={'sendInvite'}
                                    label={t('userGroup.registration.addParticipant.sendInvite')}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{padding: '16px'}}>
                        <Button color={'inherit'} onClick={onClose} disabled={isSaving}>
                            {t('form.cancel')}
                        </Button>
                        <Button color={'primary'} variant={'contained'} type={'submit'} disabled={isSaving}>
                            {t('userGroup.registration.addParticipant.button')}
                        </Button>
                    </DialogActions>
                </form>}
            </Formik>
        </Dialog>
    );
};
